import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft,
    Download,
    Upload,
    FileJson,
    Wand2,
    CheckCircle2,
    Trash2,
    Copy,
    Volume2,
    AlertTriangle,
    Layers,
} from "lucide-react";
import {
    buildStoryboard,
    loadStoredStoryboard,
    saveStoredStoryboard,
    clearStoredStoryboard,
} from "../lib/storyboard";
import { presentations, getPresentation } from "../lib/lessons";

const SAMPLE_REQUEST_BODY = `{
  "contents": [
    {
      "role": "user",
      "parts": [
        {
          "text": "Read the following transcript based on the audio profile and director's note.\\n\\n# Audio Profile\\nA warm, encouraging middle-school teacher.\\n\\n# Director's note\\nStyle: Empathetic, curious. Pace: Natural. Accent: American (Gen).\\n\\n## Scene:\\nA bright classroom, students gathered around a smart board.\\n\\n## Sample Context:\\nClear, friendly, never rushed. Tone is warm, curious, and reassuring.\\n\\n## Transcript:\\n<<<PASTE narration text HERE>>>"
        }
      ]
    }
  ],
  "generationConfig": {
    "responseModalities": ["audio"],
    "temperature": 1,
    "speech_config": {
      "voice_config": {
        "prebuilt_voice_config": { "voice_name": "Aoede" }
      }
    }
  }
}`;

const SAMPLE_OUTPUT_ENTRY = `{
  "type": "say",
  "order": 2,
  "slide_no": 2,
  "fragment_no": 0,
  "slug": "what-is",
  "text": "Plants make their own food",
  "narration": "Unlike us, plants don't eat. They make their own food right inside their leaves using sunlight, water and air.",
  "audio": "data:audio/wav;base64,UklGRiQAAA..."   // <-- you fill this
}`;

const PYTHON_SCRIPT = `#!/usr/bin/env python3
"""
generate_audio.py

Reads narration.json (downloaded from the Audio Studio page), calls Gemini
gemini-2.5-flash-preview-tts for each entry, writes the audio inline as a
base64-encoded WAV data URL in the 'audio' field, and saves storyboard.json.

Usage:
    export GEMINI_API_KEY=YOUR_KEY
    python generate_audio.py narration.json storyboard.json

Requires: pip install requests
"""

import base64
import io
import json
import os
import struct
import sys
import time
import requests

API_KEY = os.environ.get("GEMINI_API_KEY", "").strip()
if not API_KEY:
    sys.exit("Set GEMINI_API_KEY in your environment.")

MODEL = "gemini-2.5-flash-preview-tts"
URL = (
    f"https://generativelanguage.googleapis.com/v1beta/models/"
    f"{MODEL}:streamGenerateContent?key={API_KEY}"
)

VOICE = "Aoede"  # change to Kore / Puck / Charon / Fenrir if you prefer

PROFILE = (
    "A warm, encouraging middle-school teacher."
)
DIRECTOR = (
    "Style: Empathetic, curious. Pace: Natural. Accent: American (Gen).\\n"
    "## Scene:\\nA bright classroom, students gathered around a smart board.\\n"
    "## Sample Context:\\nClear, friendly, never rushed. Tone is warm, curious, "
    "and reassuring."
)


def make_request_body(narration: str) -> dict:
    return {
        "contents": [
            {
                "role": "user",
                "parts": [
                    {
                        "text": (
                            "Read the following transcript based on the audio "
                            "profile and director's note.\\n\\n# Audio Profile\\n"
                            f"{PROFILE}\\n\\n# Director's note\\n{DIRECTOR}\\n\\n"
                            f"## Transcript:\\n{narration}"
                        )
                    }
                ],
            }
        ],
        "generationConfig": {
            "responseModalities": ["audio"],
            "temperature": 1,
            "speech_config": {
                "voice_config": {"prebuilt_voice_config": {"voice_name": VOICE}}
            },
        },
    }


def pcm_to_wav(pcm: bytes, sample_rate: int = 24000) -> bytes:
    """Wrap raw 16-bit mono PCM in a WAV container."""
    num_channels = 1
    bits_per_sample = 16
    byte_rate = sample_rate * num_channels * bits_per_sample // 8
    block_align = num_channels * bits_per_sample // 8
    data_size = len(pcm)
    riff_size = 36 + data_size
    header = b"RIFF" + struct.pack("<I", riff_size) + b"WAVE"
    fmt = (
        b"fmt "
        + struct.pack(
            "<IHHIIHH",
            16,
            1,
            num_channels,
            sample_rate,
            byte_rate,
            block_align,
            bits_per_sample,
        )
    )
    data = b"data" + struct.pack("<I", data_size) + pcm
    return header + fmt + data


def generate_one(narration: str) -> str:
    body = make_request_body(narration)
    resp = requests.post(
        URL, headers={"Content-Type": "application/json"}, json=body, timeout=120
    )
    resp.raise_for_status()
    chunks = resp.json()

    pcm_buffer = io.BytesIO()
    rate = 24000
    for chunk in chunks:
        cands = chunk.get("candidates", [])
        for cand in cands:
            parts = cand.get("content", {}).get("parts", [])
            for part in parts:
                inline = part.get("inlineData") or part.get("inline_data")
                if not inline:
                    continue
                mime = inline.get("mimeType") or inline.get("mime_type") or ""
                if "rate=" in mime:
                    try:
                        rate = int(mime.split("rate=")[1].split(";")[0])
                    except Exception:
                        pass
                data = inline.get("data")
                if data:
                    pcm_buffer.write(base64.b64decode(data))

    pcm = pcm_buffer.getvalue()
    if not pcm:
        raise RuntimeError("No audio data returned by Gemini.")
    wav = pcm_to_wav(pcm, sample_rate=rate)
    return "data:audio/wav;base64," + base64.b64encode(wav).decode("ascii")


def main():
    if len(sys.argv) != 3:
        sys.exit("Usage: python generate_audio.py narration.json storyboard.json")
    in_path, out_path = sys.argv[1], sys.argv[2]
    with open(in_path, "r", encoding="utf-8") as f:
        entries = json.load(f)

    out = []
    for i, entry in enumerate(entries):
        narration = (entry.get("narration") or "").strip()
        if not narration or entry.get("type") not in ("say", "concept_check", "example", "mcq", "mcq_explanation"):
            out.append(entry)
            continue
        print(f"[{i + 1}/{len(entries)}] slide={entry.get('slide_no')} "
              f"fragment={entry.get('fragment_no')} …", flush=True)
        for attempt in range(3):
            try:
                audio = generate_one(narration)
                entry["audio"] = audio
                break
            except Exception as e:
                wait = 2 ** attempt
                print(f"   retry in {wait}s: {e}", flush=True)
                time.sleep(wait)
        out.append(entry)

    with open(out_path, "w", encoding="utf-8") as f:
        json.dump(out, f, ensure_ascii=False, indent=2)
    print(f"\\nDone. Wrote {out_path}.")


if __name__ == "__main__":
    main()
`;

/**
 * Robust download — works inside sandboxed iframes (Emergent preview) by
 * opening a Blob URL in a new tab using `noopener,noreferrer`. The new tab
 * will trigger the browser's standard "Save as…" dialog.
 *
 * Falls back to a plain anchor click if window.open is blocked.
 */
const triggerDownload = (text, filename, mime = "application/json") => {
    const blob = new Blob([text], { type: mime });
    const url = URL.createObjectURL(blob);

    // Primary: anchor with download attribute
    try {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.rel = "noopener";
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        // Don't revoke immediately — some browsers cancel the download.
        setTimeout(() => {
            try {
                document.body.removeChild(a);
            } catch {
                /* noop */
            }
            URL.revokeObjectURL(url);
        }, 2000);
        return true;
    } catch {
        /* fall through */
    }

    // Fallback: open in new tab
    try {
        window.open(url, "_blank", "noopener,noreferrer");
        return true;
    } catch {
        return false;
    }
};

const CopyButton = ({ text, label = "Copy", testid }) => {
    const [copied, setCopied] = useState(false);
    return (
        <button
            data-testid={testid}
            onClick={async () => {
                try {
                    await navigator.clipboard.writeText(text);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                } catch {
                    /* noop */
                }
            }}
            className="px-2.5 py-1 rounded-lg bg-white border border-black/[0.08] text-xs font-semibold text-[#4a5662] hover:border-[#5BC2CC] active:scale-[0.97] transition-all flex items-center gap-1.5"
        >
            <Copy className="w-3 h-3" />
            {copied ? "Copied" : label}
        </button>
    );
};

const getAudioDuration = (audioSrc) => {
    return new Promise((resolve) => {
        try {
            const audio = new Audio();
            const timeoutId = setTimeout(() => {
                try {
                    audio.src = "";
                } catch {}
                resolve(null);
            }, 10000);

            audio.onloadedmetadata = () => {
                clearTimeout(timeoutId);
                if (audio.duration && isFinite(audio.duration)) {
                    resolve(Math.round(audio.duration * 1000));
                } else {
                    resolve(null);
                }
                try {
                    audio.src = "";
                } catch {}
            };

            audio.onerror = () => {
                clearTimeout(timeoutId);
                resolve(null);
                try {
                    audio.src = "";
                } catch {}
            };

            audio.src = audioSrc;
        } catch {
            resolve(null);
        }
    });
};

const AudioStudio = () => {
    const [presentationId, setPresentationId] = useState(
        presentations[0]?.id || "photosynthesis",
    );
    const presentation = getPresentation(presentationId);
    const narration = useMemo(
        () => buildStoryboard(presentation.lesson, presentation),
        [presentation],
    );

    const [stored, setStored] = useState(null);
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [loadingDurations, setLoadingDurations] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState("");

    useEffect(() => {
        setStored(loadStoredStoryboard(presentationId));
        setError("");
        setInfo("");
        setLoadingDurations(false);
        setLoadingProgress("");
    }, [presentationId]);

    const storedCount = useMemo(
        () => (stored || []).filter((e) => e.audio).length,
        [stored],
    );

    const sayCount = narration.filter(
        (e) =>
            e.type === "say" ||
            e.type === "concept_check" ||
            e.type === "example" ||
            e.type === "mcq" ||
            e.type === "mcq_explanation",
    ).length;

    const narrationJsonText = useMemo(
        () => JSON.stringify(narration, null, 2),
        [narration],
    );

    const handleDownloadNarration = () => {
        setError("");
        const ok = triggerDownload(
            narrationJsonText,
            `${presentationId}-narration.json`,
            "application/json",
        );
        if (ok) {
            setInfo(
                "Download started. If your browser blocked it, use 'Copy JSON' next to the button.",
            );
        } else {
            setError(
                "Download was blocked. Use 'Copy JSON' to grab the contents and paste them into a file.",
            );
        }
    };

    const handleDownloadScript = () => {
        triggerDownload(PYTHON_SCRIPT, "generate_audio.py", "text/x-python");
    };

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            setInfo("Reading file...");
            setError("");
            const text = await file.text();
            const parsed = JSON.parse(text);
            if (!Array.isArray(parsed)) {
                throw new Error(
                    "File should be a JSON array of storyboard entries.",
                );
            }
            const ok = parsed.every(
                (entry) =>
                    entry &&
                    typeof entry === "object" &&
                    typeof entry.type === "string",
            );
            if (!ok) {
                throw new Error(
                    "Each entry must be an object with a 'type' field.",
                );
            }

            const audioEntries = parsed.filter((p) => p.audio);
            const needsCalculation = audioEntries.filter(
                (p) => p.durationMs === undefined || p.durationMs === null,
            );

            if (needsCalculation.length > 0) {
                setLoadingDurations(true);
                setInfo(`Analyzing audio durations: 0 / ${needsCalculation.length}...`);

                let calculated = 0;
                for (const entry of parsed) {
                    if (entry.audio && (entry.durationMs === undefined || entry.durationMs === null)) {
                        setLoadingProgress(
                            `Analyzing slide ${entry.slide_no} fragment ${
                                entry.fragment_no || 0
                            }...`,
                        );
                        const durationMs = await getAudioDuration(entry.audio);
                        if (durationMs) {
                            entry.durationMs = durationMs;
                        }
                        calculated++;
                        setInfo(`Analyzing audio durations: ${calculated} / ${needsCalculation.length}...`);
                    }
                }
            }

            saveStoredStoryboard(presentationId, parsed);
            setStored(parsed);
            setError("");
            setInfo(
                `Loaded ${parsed.filter((p) => p.audio).length} of ${parsed.length} audio entries with precise durations for ${presentation.title}.`,
            );
        } catch (err) {
            setError(err.message || "Failed to parse file.");
        } finally {
            setLoadingDurations(false);
            setLoadingProgress("");
            e.target.value = "";
        }
    };

    const onClear = () => {
        clearStoredStoryboard(presentationId);
        setStored(null);
        setInfo("Audio cleared for this presentation.");
    };

    return (
        <div className="min-h-screen ambient-bg grain pb-16">
            <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-black/[0.06]">
                <div className="max-w-[1100px] mx-auto px-5 py-3 flex items-center gap-4">
                    <Link
                        to="/"
                        data-testid="audio-studio-back-link"
                        className="flex items-center gap-2 text-sm font-semibold text-[#14202a] hover:text-[#2E8E9A] transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </Link>
                    <div className="h-5 w-px bg-black/10" />
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg sparkl-gradient flex items-center justify-center">
                            <Volume2 className="w-4 h-4 text-white" />
                        </div>
                        <p className="text-base font-semibold tracking-tight">
                            Audio Studio
                        </p>
                    </div>
                </div>
            </header>

            <div className="max-w-[1100px] mx-auto px-5 pt-10 space-y-8">
                <div>
                    <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-3">
                        Bring your lesson{" "}
                        <span className="sparkl-text-gradient">to life.</span>
                    </h1>
                    <p className="text-lg text-[#4a5662] max-w-2xl leading-relaxed">
                        Download the narration script, generate audio with
                        your Gemini key, then upload the finished JSON. Each
                        presentation gets its own audio set.
                    </p>
                </div>

                {/* Presentation picker */}
                <div className="rounded-3xl bg-white border border-black/[0.05] soft-shadow p-5 md:p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Layers className="w-3.5 h-3.5 text-[#5BC2CC]" />
                        <p className="text-[10px] uppercase tracking-[0.22em] text-[#14202a] font-semibold">
                            Presentation
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {presentations.map((p) => {
                            const active = p.id === presentationId;
                            const ps = loadStoredStoryboard(p.id);
                            const audioCount = (ps || []).filter(
                                (e) => e.audio,
                            ).length;
                            return (
                                <button
                                    key={p.id}
                                    data-testid={`presentation-${p.id}-btn`}
                                    onClick={() => setPresentationId(p.id)}
                                    className={`text-left p-4 rounded-2xl border-2 transition-all ${
                                        active
                                            ? "border-[#5BC2CC] bg-[#E0F4F4]"
                                            : "border-black/[0.05] bg-white hover:border-[#B8E8E8]"
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <p className="text-base font-semibold tracking-tight">
                                                {p.title}
                                            </p>
                                            <p className="text-xs text-[#4a5662] mt-1">
                                                {p.grade} · {p.chapter}
                                            </p>
                                        </div>
                                        {audioCount > 0 && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#DCEFEC] text-[#2D7A6A] text-[9px] uppercase tracking-[0.18em] font-semibold">
                                                <CheckCircle2 className="w-2.5 h-2.5" />
                                                {audioCount} audio
                                            </span>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                    {presentations.length === 1 && (
                        <p className="text-[11px] text-[#4a5662] italic mt-3">
                            More presentations coming soon — add them in{" "}
                            <code>src/lib/lessons.js</code>.
                        </p>
                    )}
                </div>

                {/* Status */}
                {stored ? (
                    <div
                        data-testid="audio-status-loaded"
                        className="p-5 rounded-2xl bg-white border border-[#5BC2CC]/40 flex items-center justify-between"
                    >
                        <div className="flex items-center gap-3">
                            <CheckCircle2 className="w-6 h-6 text-[#2E8E9A]" />
                            <div>
                                <p className="text-sm font-semibold tracking-tight">
                                    Audio loaded for {presentation.title}
                                </p>
                                <p className="text-xs text-[#4a5662]">
                                    {storedCount} of {stored.length} entries
                                    have audio. Students will hear narration
                                    automatically.
                                </p>
                            </div>
                        </div>
                        <button
                            data-testid="clear-storyboard-btn"
                            onClick={onClear}
                            className="px-3 py-2 rounded-lg bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100 active:scale-[0.97] transition-all flex items-center gap-1.5"
                        >
                            <Trash2 className="w-3 h-3" />
                            Remove audio
                        </button>
                    </div>
                ) : (
                    <div className="p-5 rounded-2xl bg-[#FFF1E5] border border-[#FFA987]/40 flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-[#C75730]" />
                        <p className="text-sm text-[#14202a]">
                            No audio uploaded for{" "}
                            <span className="font-semibold">
                                {presentation.title}
                            </span>{" "}
                            yet. Follow the steps below.
                        </p>
                    </div>
                )}

                {info && (
                    <div className="p-3 rounded-xl bg-[#DCEFEC] border border-[#5BC2CC]/30 text-[#1F5E50] text-xs">
                        {info}
                    </div>
                )}
                {loadingProgress && (
                    <div className="p-3 rounded-xl bg-[#FFF9E6] border border-[#F5C242]/30 text-[#8A6D1C] text-xs animate-pulse">
                        ⚡ {loadingProgress}
                    </div>
                )}
                {error && (
                    <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">
                        {error}
                    </div>
                )}

                {/* Step 1 — download */}
                <Section
                    step="1"
                    title={`Download narration for ${presentation.title}`}
                    body={`A JSON file with every line your AI tutor will speak, indexed by slide and fragment. ${sayCount} narrated entries.`}
                >
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            data-testid="download-narration-btn"
                            onClick={handleDownloadNarration}
                            className="px-4 py-2.5 rounded-xl sparkl-gradient text-white font-semibold text-sm shadow-[0_4px_14px_rgba(91,194,204,0.3)] hover:shadow-[0_6px_18px_rgba(91,194,204,0.4)] active:scale-[0.98] transition-all flex items-center gap-1.5"
                        >
                            <Download className="w-4 h-4" />
                            {presentationId}-narration.json
                        </button>
                        <CopyButton
                            text={narrationJsonText}
                            label="Copy JSON"
                            testid="copy-narration-btn"
                        />
                        <span className="text-xs text-[#4a5662]">
                            {sayCount} entries · ~5 min total speech
                        </span>
                    </div>
                    <p className="text-[11px] text-[#4a5662] mt-3">
                        If the download doesn't trigger (preview iframes can
                        block them), tap <strong>Copy JSON</strong> instead
                        and paste into a new file called{" "}
                        <code>{presentationId}-narration.json</code>.
                    </p>
                </Section>

                {/* Step 2 — generation script */}
                <Section
                    step="2"
                    title="Generate audio with Gemini"
                    body="Run the helper script with your Gemini API key. It calls gemini-2.5-flash-preview-tts for every entry and embeds the audio as base64 in the JSON."
                >
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                        <button
                            data-testid="download-script-btn"
                            onClick={handleDownloadScript}
                            className="px-4 py-2.5 rounded-xl bg-white border-2 border-[#5BC2CC] text-[#2E8E9A] font-semibold text-sm hover:bg-[#E0F4F4] active:scale-[0.98] transition-all flex items-center gap-1.5"
                        >
                            <FileJson className="w-4 h-4" />
                            generate_audio.py
                        </button>
                        <CopyButton
                            text={PYTHON_SCRIPT}
                            label="Copy script"
                            testid="copy-script-btn"
                        />
                    </div>
                    <pre className="text-[11px] bg-[#0D1418] text-[#DCEFEC] rounded-xl p-4 overflow-x-auto leading-relaxed">{`# 1) Get a free Gemini key at https://aistudio.google.com/app/apikey
# 2) Set it
export GEMINI_API_KEY=YOUR_KEY_HERE

# 3) Install once
pip install requests

# 4) Run
python generate_audio.py ${presentationId}-narration.json ${presentationId}-storyboard.json
# Output: ${presentationId}-storyboard.json — every entry now has an 'audio' field.`}</pre>
                </Section>

                {/* Step 3 — request body sample */}
                <Section
                    step="3"
                    title="What gets sent to Gemini"
                    body={`The script POSTs this body to gemini-2.5-flash-preview-tts (voice: "Aoede"). Swap voices inside the script — Kore, Puck, Charon, Fenrir.`}
                >
                    <div className="relative">
                        <div className="absolute top-2 right-2">
                            <CopyButton
                                text={SAMPLE_REQUEST_BODY}
                                label="Copy JSON"
                                testid="copy-sample-body-btn"
                            />
                        </div>
                        <pre className="text-[11px] bg-[#0D1418] text-[#DCEFEC] rounded-xl p-4 overflow-x-auto leading-relaxed">
                            {SAMPLE_REQUEST_BODY}
                        </pre>
                    </div>
                </Section>

                {/* Step 4 — sample output */}
                <Section
                    step="4"
                    title="What a finished entry looks like"
                    body="Each entry must include a 'type' field. For 'say', 'concept_check', and 'example' entries, include the narration text and an 'audio' URL (https:// link or data: URL)."
                >
                    <pre className="text-[11px] bg-[#0D1418] text-[#DCEFEC] rounded-xl p-4 overflow-x-auto leading-relaxed">
                        {SAMPLE_OUTPUT_ENTRY}
                    </pre>
                </Section>

                <Section
                    step="5"
                    title={`Upload storyboard for ${presentation.title}`}
                    body="Drop in the storyboard.json the script produced. Stored locally — never uploaded to a server."
                >
                    {loadingDurations ? (
                        <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 text-slate-500 font-semibold text-sm border border-slate-200">
                            <span className="w-4 h-4 rounded-full border-2 border-slate-300 border-t-slate-500 animate-spin" />
                            Calculating audio durations...
                        </div>
                    ) : (
                        <>
                            <label
                                htmlFor="storyboard-upload"
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl sparkl-gradient text-white font-semibold text-sm shadow-[0_4px_14px_rgba(91,194,204,0.3)] hover:shadow-[0_6px_18px_rgba(91,194,204,0.4)] active:scale-[0.98] transition-all cursor-pointer"
                            >
                                <Upload className="w-4 h-4" />
                                Upload {presentationId}-storyboard.json
                            </label>
                            <input
                                id="storyboard-upload"
                                type="file"
                                accept="application/json,.json"
                                onChange={onUpload}
                                data-testid="upload-storyboard-input"
                                className="hidden"
                            />
                        </>
                    )}
                    <p className="text-[11px] text-[#4a5662] mt-3">
                        Stored at{" "}
                        <code>{`localStorage["sparkl-storyboard-${presentationId}-v1"]`}</code>
                        .
                    </p>
                </Section>

                <div className="rounded-2xl border border-black/[0.05] bg-white/70 p-5 flex items-start gap-3">
                    <Wand2 className="w-4 h-4 text-[#5BC2CC] mt-0.5" />
                    <div className="text-sm text-[#4a5662] leading-relaxed">
                        <p className="font-semibold text-[#14202a] mb-1">
                            Pro tip
                        </p>
                        Want different voices on different slides? The
                        narration JSON has a <code>slug</code> field — tweak
                        the script to pick voices per slug.
                    </div>
                </div>
            </div>
        </div>
    );
};

const Section = ({ step, title, body, children }) => (
    <div className="rounded-3xl bg-white border border-black/[0.05] soft-shadow p-6 md:p-7">
        <div className="flex items-start gap-4 mb-4">
            <div className="shrink-0 w-9 h-9 rounded-xl sparkl-gradient flex items-center justify-center text-white font-bold text-sm">
                {step}
            </div>
            <div>
                <h2 className="text-xl font-semibold tracking-tight">
                    {title}
                </h2>
                <p className="text-sm text-[#4a5662] mt-1 leading-relaxed">
                    {body}
                </p>
            </div>
        </div>
        <div>{children}</div>
    </div>
);

export default AudioStudio;
